import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { ReportExportButtons } from '../../components/ReportExportButtons'
import { Callout, CircularProgress, FilterBar, Page, PageHeader, TextField } from '../../components/ui'

/**
 * R3-22 — guscio comune dei report dedicati: titolo con «Esporta» (CSV, Excel, PDF dalla tabella a video),
 * periodo, caricamento ed errore. Col flag `report.dedicati` spento il server risponde `{ enabled: false }`.
 */
export type DedicatedLoad<T> = (from?: string, to?: string) => Promise<T | { enabled: false }>

export const pct = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : `${(v * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%`

export const eur = (v: number | undefined): string =>
  v === undefined ? '—' : v.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })

export function DedicatedReportShell<T extends { enabled: true }>({
  title, icon, load, children,
}: {
  title: string
  icon: string
  load: DedicatedLoad<T>
  children: (data: T) => ReactNode
}) {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [data, setData] = useState<T | null>(null)
  const [disabled, setDisabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const run = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await load(from || undefined, to || undefined)
      if (res.enabled) { setData(res as T); setDisabled(false) } else { setData(null); setDisabled(true) }
    } catch (e) {
      setData(null)
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
    // Caricamento iniziale: i filtri successivi si applicano col pulsante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load])

  useEffect(() => { void run() }, [run])

  return (
    <Page>
      <PageHeader title={title} icon={icon} actions={<ReportExportButtons title={title} />} />
      <FilterBar onSubmit={() => void run()}>
        <TextField label="Da" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <TextField label="A" type="date" value={to} onChange={e => setTo(e.target.value)} />
      </FilterBar>
      <div className="text-muted md-typescale-body-small">Senza date: dal primo del mese a oggi. Massimo 366 giorni.</div>
      {loading ? (
        <div className="text-center py-4"><div className="d-inline-block"><CircularProgress size="md" label="Caricamento..." /></div></div>
      ) : error ? (
        <Callout tone="warning">Report non disponibile. {error}</Callout>
      ) : disabled ? (
        <Callout tone="info">Questo report non è ancora attivo.</Callout>
      ) : data ? children(data) : null}
    </Page>
  )
}

export function Limitations({ items }: { items?: string[] | undefined }) {
  if (!items || items.length === 0) return null
  return <ul className="md-typescale-body-small text-muted">{items.map(n => <li key={n}>{n}</li>)}</ul>
}
